import React from 'react'
import { Head, Link, router } from '@inertiajs/react'
import {
  Container, Typography, Box, Button, Paper, Chip, Stack, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import LanguageIcon from '@mui/icons-material/Language'
import GroupsIcon from '@mui/icons-material/Groups'

interface Group {
  id: number
  name: string
  gender: string
  group_type: string
  member_count: number
}

interface Company {
  id: number
  name: string
  country: string | null
  description: string | null
  website_url: string | null
  group_count: number
  groups: Group[]
  created_at: string
}

interface Props {
  company: Company
}

const genderLabels: Record<string, string> = { girl: 'Girl', boy: 'Boy', mixed: 'Mixed' }
const genderColors: Record<string, 'error' | 'info' | 'warning'> = { girl: 'error', boy: 'info', mixed: 'warning' }
const groupTypeLabels: Record<string, string> = { group: 'Group', band: 'Band' }

export default function CompanyShow({ company }: Props) {
  const handleDelete = () => {
    if (confirm(`Are you sure you want to delete "${company.name}"? This will also delete all its groups.`)) {
      router.delete(`/companies/${company.id}`)
    }
  }

  return (
    <>
      <Head title={company.name} />
      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 4 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Button component={Link} href="/companies" startIcon={<ArrowBackIcon />}>
            Back to Companies
          </Button>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              component={Link}
              href={`/companies/${company.id}/edit`}
              variant="contained"
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
            <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
              Delete
            </Button>
          </Box>
        </Box>

        <Paper sx={{ p: 4, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <Typography variant="h5">{company.name}</Typography>
            {company.country && (
              <Chip label={company.country} size="small" variant="outlined" />
            )}
          </Box>

          {company.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {company.description}
            </Typography>
          )}

          {company.website_url && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageIcon sx={{ fontSize: 16 }} color="action" />
              <Typography
                variant="body2"
                component="a"
                href={company.website_url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                {company.website_url}
              </Typography>
            </Box>
          )}
        </Paper>

        <Paper sx={{ p: 4 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
            <GroupsIcon color="action" />
            <Typography variant="h6">Groups ({company.group_count})</Typography>
          </Stack>

          {company.groups.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center', fontStyle: 'italic' }}>
              No groups yet.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Gender</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Members</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {company.groups.map((group) => (
                    <TableRow key={group.id} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell>
                        <Typography
                          variant="body2"
                          component={Link}
                          href={`/groups/${group.id}`}
                          sx={{ fontWeight: 500, color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {group.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={genderLabels[group.gender] || group.gender}
                          size="small"
                          color={genderColors[group.gender] || 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {groupTypeLabels[group.group_type] || group.group_type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {group.member_count}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.disabled">
            Added {new Date(company.created_at).toLocaleDateString()}
          </Typography>
        </Box>
      </Container>
    </>
  )
}
